import type { AdminRole } from '../api/types';

/**
 * Capability model for the CMS. This mirrors the backend's authoritative gate:
 * only SUPER_ADMIN + ADMIN (the `ELEVATED` set in admin.controller.ts) may
 * perform state-changing actions today; SUPPORT and FINANCE are read-only.
 *
 * The UI uses this to HIDE/DISABLE controls a role cannot use, but the server
 * @Roles guard remains the source of truth — never trust the client alone.
 *
 * NOTE (surfaced by recon): FINANCE currently cannot act on finance routes on
 * the backend. We model an intended `finance.*` capability so the UI is ready
 * the moment the backend RBAC is corrected; until then it resolves via ELEVATED.
 */
export type Capability =
  | 'users.write'        // adjust balance, ban
  | 'finance.write'      // approve/reject withdrawals, confirm recharges
  | 'games.write'        // force-draw, odds, config
  | 'content.write'      // banners, notices, settings, CS
  | 'payments.write'     // toggle / manage payment methods
  | 'engagement.write'   // activities, vip, point mall
  | 'affiliate.write'    // commission config
  | 'admins.manage'      // manage admin accounts + roles
  | 'settings.write'     // platform settings, feature flags
  | 'compliance.write'   // KYC review, responsible-gambling limits/exclusions
  | 'risk.write'         // triage/resolve risk & AML alerts
  | 'audit.read'
  | 'reports.export'
  | 'support.write';

const ELEVATED: AdminRole[] = ['SUPER_ADMIN', 'ADMIN'];
// FINANCE can perform its core money duties (withdrawal review, recharge
// confirm, balance adjust) — mirrors the backend FINANCE_OPS gate.
const FINANCE_OPS: AdminRole[] = ['SUPER_ADMIN', 'ADMIN', 'FINANCE'];

const MATRIX: Record<Capability, AdminRole[]> = {
  'users.write': ELEVATED,
  'finance.write': FINANCE_OPS,
  'games.write': ELEVATED,
  'content.write': ELEVATED,
  'payments.write': ELEVATED,
  'engagement.write': ELEVATED,
  'affiliate.write': ELEVATED,
  'admins.manage': ['SUPER_ADMIN'],
  'settings.write': ELEVATED,
  'compliance.write': ['SUPER_ADMIN', 'ADMIN', 'SUPPORT'],
  'risk.write': ELEVATED,
  'audit.read': ['SUPER_ADMIN', 'ADMIN', 'SUPPORT', 'FINANCE'],
  'reports.export': ['SUPER_ADMIN', 'ADMIN', 'FINANCE'],
  'support.write': ['SUPER_ADMIN', 'ADMIN', 'SUPPORT'],
};

export function can(role: AdminRole | null | undefined, capability: Capability): boolean {
  if (!role) return false;
  return MATRIX[capability]?.includes(role) ?? false;
}

export const ROLE_LABELS: Record<AdminRole, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  SUPPORT: 'Support',
  FINANCE: 'Finance',
};

/** Tailwind classes for the role badge. */
export const ROLE_BADGE: Record<AdminRole, string> = {
  SUPER_ADMIN: 'bg-primary/15 text-primary',
  ADMIN: 'bg-info/15 text-info',
  SUPPORT: 'bg-muted text-muted-foreground',
  FINANCE: 'bg-success/15 text-success',
};
